import type { RouterRoute } from '@brainfocus/web/router/router';
import type { RouterParentState } from '@brainfocus/web/router/router_context';
import { resolvePathname } from '@brainfocus/web/router/url_utils';

/**
 * The pathname part of a navigation target: the query string and the hash are
 * not part of the route (e.g. the EEG preview's `?t=` resume position, #374).
 */
export function routePathname(path: string): string {
    return path.split(/[?#]/, 1)[0] ?? path;
}

/**
 * Find a route that matches the given pathname (any query string or hash is
 * ignored).
 */
export function findMatchingRoute(
    routes: RouterRoute[],
    context: RouterParentState,
    pathname: string,
): { route: RouterRoute; params: Record<string, string> } | null {
    pathname = routePathname(pathname);
    if (!pathname.endsWith('/')) {
        pathname = pathname + '/';
    }

    for (const route of routes) {
        let resolvedPath = resolvePathname(context.pathPrefix, route.path);
        if (resolvedPath.match(/[^/]\$$/)) {
            resolvedPath = resolvedPath.replace('$', '/$');
        } else if (resolvedPath.match(/[^/$]$/)) {
            resolvedPath = resolvedPath + '/';
        }

        const wildSub = resolvedPath.replaceAll('/', '\\/').replace(/:([a-zA-Z0-9]+)/g, '([a-zA-Z0-9\\-._]+)');
        const expStr = `^${wildSub}`;
        const exp = new RegExp(expStr);
        const match = pathname.match(exp);

        if (match) {
            const guards = route.guard && route.guard instanceof Array
                ? route.guard
                : route.guard
                    ? [route.guard]
                    : [];
            const failingGuard = guards.map(g => g()).find(result => result !== true);

            if (failingGuard === undefined) {
                // Extract params from URL
                const params = extractParams(resolvedPath, match);
                return { route, params };
            }
        }
    }

    return null;
}

/**
 * Extract parameters from a route match
 */
function extractParams(pattern: string, match: RegExpMatchArray): Record<string, string> {
    const params: Record<string, string> = {};
    const patternParts = pattern.split('/');

    let paramIndex = 1; // match[0] is the full match, params start at index 1
    patternParts.forEach((part) => {
        if (part.startsWith(':')) {
            const paramName = part.substring(1);
            const value = match[paramIndex];
            if (value !== undefined) {
                params[paramName] = value;
            }
            paramIndex++;
        }
    });

    return params;
}
